import React, { useState, useEffect } from "react";
import "./RegistryPage.css";
import { apiService, getApiMode } from "../utils/apiService";

interface RegistryItem {
  item_id: string;
  name: string;
  description: string;
  link: string;
  picture_link: string;
  is_purchased: boolean;
  purchased_who: string;
  category: string;
  order?: number;
  price?: number;
  tracking_link?: string;
  is_group_gift?: boolean;
  group_gift_target?: number;
  current_pledged?: number;
  venmo_link?: string;
  qr_code?: string;
  pledges?: Array<{
    name: string;
    amount: number;
    timestamp: string;
  }>;
}

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (purchaser: string) => void;
  itemName: string;
}

interface ContributeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (contributorName: string, amount: number) => void;
  itemName: string;
  currentPledged: number;
  targetAmount: number;
  venmoLink?: string;
}

interface QRModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  imageSrc: string;
}

const ProgressBar: React.FC<{
  current: number;
  target: number;
  className?: string;
}> = ({ current, target, className = "" }) => {
  const percentage = Math.min((current / target) * 100, 100);
  const remaining = Math.max(target - current, 0);

  return (
    <div className={`progress-container ${className}`}>
      <div className="progress-info">
        <span className="progress-current">${current.toFixed(2)}</span>
        <span className="progress-target">of ${target.toFixed(2)}</span>
      </div>
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="progress-remaining">
        {remaining > 0 ? `$${remaining.toFixed(2)} remaining` : "Goal reached!"}
      </div>
    </div>
  );
};

const QRModal: React.FC<QRModalProps> = ({ isOpen, onClose, title, imageSrc }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay qr-modal-overlay" onClick={onClose}>
      <div className="modal-content qr-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body qr-modal-body">
          <div className="qr-code-container">
            <img src={imageSrc} alt={title} className="qr-code-image" />
          </div>
          <p className="qr-instructions">
            Scan this QR code with your banking app to send payment via Zelle
          </p>
        </div>
      </div>
    </div>
  );
};

const ContributeModal: React.FC<ContributeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  currentPledged,
  targetAmount,
  venmoLink,
}) => {
  const [contributorName, setContributorName] = useState("");
  const [amount, setAmount] = useState("");
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const contributionAmount = parseFloat(amount);
    if (contributorName.trim() && contributionAmount > 0) {
      onConfirm(contributorName.trim(), contributionAmount);
      setContributorName("");
      setAmount("");
    }
  };

  const handleClose = () => {
    setContributorName("");
    setAmount("");
    onClose();
  };

  if (!isOpen) return null;

  const remaining = Math.max(targetAmount - currentPledged, 0);

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Make a Contribution</h3>
          <button className="modal-close" onClick={handleClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p>
            Contribute towards <strong>{itemName}</strong>
          </p>
          <ProgressBar
            current={currentPledged}
            target={targetAmount}
            className="modal-progress"
          />
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={contributorName}
              onChange={(e) => setContributorName(e.target.value)}
              placeholder="Enter your name"
              className="modal-input"
              autoFocus
            />
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Amount (max: $${remaining.toFixed(2)})`}
              className="modal-input"
              min="0.01"
              max={remaining}
              step="0.01"
            />
            <div className="modal-payment-methods">
              {venmoLink && (
                <a
                  href={venmoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modal-payment-btn venmo-btn"
                >
                  Pay via Venmo
                </a>
              )}
              <button
                type="button"
                className="modal-payment-btn zelle-btn"
                onClick={() => setQrModalOpen(true)}
              >
                Pay via Zelle (QR Code)
              </button>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="modal-confirm"
                disabled={!contributorName.trim() || !amount || parseFloat(amount) <= 0}
              >
                Confirm Contribution
              </button>
            </div>
          </form>
        </div>
      </div>

      <QRModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title="Pay via Zelle"
        imageSrc="/zelle.png"
      />
    </div>
  );
};

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
}) => {
  const [purchaser, setPurchaser] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (purchaser.trim()) {
      onConfirm(purchaser.trim());
      setPurchaser("");
    }
  };

  const handleClose = () => {
    setPurchaser("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Mark Item as Purchased</h3>
          <button className="modal-close" onClick={handleClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p>
            Who is purchasing <strong>{itemName}</strong>?
          </p>
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={purchaser}
              onChange={(e) => setPurchaser(e.target.value)}
              placeholder="Enter your name"
              className="modal-input"
              autoFocus
            />
            <div className="modal-actions">
              <button
                type="button"
                className="modal-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="modal-confirm"
                disabled={!purchaser.trim()}
              >
                Confirm Purchase
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const RegistryPage: React.FC = () => {
  const [registryItems, setRegistryItems] = useState<RegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [contributeModalOpen, setContributeModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RegistryItem | null>(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | "off">("off");
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [showPurchased, setShowPurchased] = useState<boolean>(true);

  useEffect(() => {
    fetchRegistryItems();
  }, []);

  const fetchRegistryItems = async () => {
    try {
      setLoading(true);

      // Debug: Log API mode
      console.log("API Mode:", getApiMode());
      console.log("Environment variable:", process.env.REACT_APP_RSVP_API_ENDPOINT);

      const data = await apiService.getRegistry();
      setRegistryItems(data.registry_items || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching registry items:", err);
      setError("Failed to load registry items");
      setLoading(false);
    }
  };

  const handlePurchaseUpdate = async (
    itemId: string,
    isPurchased: boolean,
    purchasedBy: string
  ) => {
    try {
      await apiService.updateRegistryItem(itemId, isPurchased, purchasedBy);

      // Update the local state
      setRegistryItems((prevItems) =>
        prevItems.map((item) =>
          item.item_id === itemId
            ? { ...item, is_purchased: isPurchased, purchased_who: purchasedBy }
            : item
        )
      );
    } catch (err) {
      console.error("Error updating purchase status:", err);
      setError("Failed to update purchase status");
    }
  };

  const handleContributionUpdate = async (
    itemId: string,
    contributorName: string,
    amount: number
  ) => {
    try {
      await apiService.pledgeRegistryItem(itemId, contributorName, amount);

      // Update the local state
      setRegistryItems((prevItems) =>
        prevItems.map((item) => {
          if (item.item_id === itemId) {
            const newPledges = [...(item.pledges || []), {
              name: contributorName,
              amount: amount,
              timestamp: new Date().toISOString(),
            }];
            const newCurrentPledged = (item.current_pledged || 0) + amount;
            return {
              ...item,
              current_pledged: newCurrentPledged,
              pledges: newPledges
            };
          }
          return item;
        })
      );
    } catch (err) {
      console.error("Error updating contribution:", err);
      setError("Failed to update contribution");
    }
  };

  const openPurchaseModal = (item: RegistryItem) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const openContributeModal = (item: RegistryItem) => {
    setSelectedItem(item);
    setContributeModalOpen(true);
  };

  const handlePurchaseConfirm = (purchaser: string) => {
    if (selectedItem) {
      handlePurchaseUpdate(selectedItem.item_id, true, purchaser);
      setModalOpen(false);
      setSelectedItem(null);
    }
  };

  const handleContributionConfirm = (contributorName: string, amount: number) => {
    if (selectedItem) {
      handleContributionUpdate(selectedItem.item_id, contributorName, amount);
      setContributeModalOpen(false);
      setSelectedItem(null);
    }
  };

  // Get unique categories from registry items
  const getUniqueCategories = () => {
    const categories = registryItems
      .map((item) => item.category)
      .filter((cat) => cat && cat.trim() !== "");
    return ["all", ...Array.from(new Set(categories))];
  };

  // Sort items based on current sort settings
  const sortItems = (items: RegistryItem[]) => {
    // If sort is off, return items in original order
    if (sortOrder === "off") {
      return items;
    }

    return [...items].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "category":
          aValue = (a.category || "").toLowerCase();
          bValue = (b.category || "").toLowerCase();
          break;
        case "price":
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) {
        return sortOrder === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Filter items by selected category and purchase status
  const getFilteredItems = () => {
    let filtered = registryItems;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }

    // Filter by purchase status
    if (!showPurchased) {
      filtered = filtered.filter((item) => !item.is_purchased);
    }

    return sortItems(filtered);
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCategoryDropdownOpen(false);
  };

  const toggleCategoryDropdown = () => {
    setCategoryDropdownOpen(!categoryDropdownOpen);
  };

  const getSelectedCategoryDisplay = () => {
    if (selectedCategory === "all") {
      return "All Categories";
    }
    return selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1).toLowerCase();
  };

  const handleSortSelect = (sortOption: string) => {
    setSortBy(sortOption);
    setSortDropdownOpen(false);
  };

  const toggleSortDropdown = () => {
    setSortDropdownOpen(!sortDropdownOpen);
  };

  const getSelectedSortDisplay = () => {
    const sortLabels: { [key: string]: string } = {
      name: "Name",
      category: "Category",
      price: "Price"
    };
    if (sortOrder === "off") {
      return `${sortLabels[sortBy] || "Name"} (Off)`;
    }
    return `${sortLabels[sortBy] || "Name"} (${sortOrder === "asc" ? "A-Z" : "Z-A"})`;
  };

  const toggleSortOrder = () => {
    if (sortOrder === "off") {
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("off");
    }
  };

  const togglePurchasedFilter = () => {
    setShowPurchased(!showPurchased);
  };

  if (loading) {
    return (
      <div className="registry-page">
        <div className="container">
          <h1>Registry</h1>
          <div className="loading">Loading registry items...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="registry-page">
        <div className="container">
          <h1>Registry</h1>
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="registry-page">
      <div className="container">
        <h1>Registry</h1>
        {getApiMode() === 'mock' && (
          <div className="dev-mode-banner">
            🔧 Development Mode - Using Mock Data
          </div>
        )}
        <p className="registry-intro">
          Your presence is our present!
          <br />
          Please do not feel obligated to bring a gift, especially if you are
          traveling a long distance.
          <br />
          However if you do wish to give a gift we have our registry below.
          <br />
          Please have items shipped to
          <br />
          573 Westfield Way, Unit E
          <br />
          Pewaukee, WI 53072
        </p>

        {/* Category Filter and Sort Controls */}
        {registryItems.length > 0 && (
          <div className="category-filter">
            <div className="filter-controls">
              <div className="filter-group">
                <label htmlFor="category-dropdown">Filter by category: </label>
                <div className="custom-dropdown category-dropdown">
                  <button
                    type="button"
                    id="category-dropdown"
                    className={`dropdown-button ${
                      categoryDropdownOpen ? "open" : ""
                    }`}
                    onClick={toggleCategoryDropdown}
                  >
                    <span className="dropdown-text">
                      {getSelectedCategoryDisplay()}
                    </span>
                    <span className="dropdown-arrow">▼</span>
                  </button>
                </div>

                {categoryDropdownOpen && (
                  <div className="dropdown-options category-dropdown-options">
                    {getUniqueCategories().map((category) => (
                      <div
                        key={category}
                        className={`dropdown-option ${
                          selectedCategory === category ? "selected" : ""
                        }`}
                        onClick={() => handleCategorySelect(category)}
                      >
                        {category === "all" ? "All Categories" : category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-group">
                <label htmlFor="sort-dropdown">Sort by: </label>
                <div className="custom-dropdown sort-dropdown">
                  <button
                    type="button"
                    id="sort-dropdown"
                    className={`dropdown-button ${
                      sortDropdownOpen ? "open" : ""
                    }`}
                    onClick={toggleSortDropdown}
                  >
                    <span className="dropdown-text">
                      {getSelectedSortDisplay()}
                    </span>
                    <span className="dropdown-arrow">▼</span>
                  </button>
                </div>

                {sortDropdownOpen && (
                  <div className="dropdown-options sort-dropdown-options">
                    <div
                      className={`dropdown-option ${
                        sortBy === "name" ? "selected" : ""
                      }`}
                      onClick={() => handleSortSelect("name")}
                    >
                      Name
                    </div>
                    <div
                      className={`dropdown-option ${
                        sortBy === "category" ? "selected" : ""
                      }`}
                      onClick={() => handleSortSelect("category")}
                    >
                      Category
                    </div>
                    <div
                      className={`dropdown-option ${
                        sortBy === "price" ? "selected" : ""
                      }`}
                      onClick={() => handleSortSelect("price")}
                    >
                      Price
                    </div>
                  </div>
                )}
              </div>

              <button
                className={`sort-order-toggle ${sortOrder !== "off" ? "active" : ""}`}
                onClick={toggleSortOrder}
                title={`Sort ${sortOrder === "off" ? "Ascending" : sortOrder === "asc" ? "Descending" : "Off"}`}
              >
                {sortOrder === "off" ? "○" : sortOrder === "asc" ? "↑" : "↓"}
              </button>

              <div className="filter-group">
                <label htmlFor="purchased-toggle">Show purchased items: </label>
                <button
                  type="button"
                  id="purchased-toggle"
                  className={`purchased-toggle ${showPurchased ? "active" : ""}`}
                  onClick={togglePurchasedFilter}
                  title={showPurchased ? "Hide purchased items" : "Show purchased items"}
                >
                  {showPurchased ? "✓" : "✗"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="registry-grid">
          {getFilteredItems().map((item) => (
            <div
              key={item.item_id}
              className={`registry-item ${
                item.is_purchased ? "purchased" : ""
              }`}
            >
              <div className="item-image">
                <img src={item.picture_link} alt={item.name} />
                {item.is_purchased && (
                  <div className="purchased-badge">Purchased</div>
                )}
              </div>

              <div className="item-details">
                <h3>{item.name}</h3>
                <div className="item-meta">
                  {item.category && (
                    <span className="item-category">{item.category.charAt(0).toUpperCase() + item.category.slice(1).toLowerCase()}</span>
                  )}
                  {item.price && item.price > 0 && (
                    <div className="item-price">${item.price}</div>
                  )}
                </div>
                <p className="item-description">{item.description}</p>

                {/* Group Gift Section */}
                {item.is_group_gift && (
                  <div className="group-gift-section">
                    <div className="group-gift-header">
                      <span className="group-gift-badge">Group Gift</span>
                    </div>

                    <ProgressBar
                      current={item.current_pledged || 0}
                      target={item.group_gift_target || 0}
                      className="item-progress"
                    />

                    {item.pledges && item.pledges.length > 0 && (
                      <div className="pledges-summary">
                        <p className="contribution-count">
                          {item.pledges.length} {item.pledges.length === 1 ? 'person has' : 'people have'} contributed
                        </p>
                      </div>
                    )}


                    <div className="group-gift-actions">
                      <button
                        className="contribute-btn"
                        onClick={() => openContributeModal(item)}
                        disabled={(item.current_pledged || 0) >= (item.group_gift_target || 0)}
                      >
                        {(item.current_pledged || 0) >= (item.group_gift_target || 0)
                          ? "Goal Reached!"
                          : "Make a Contribution"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Regular Purchase Section */}
                {!item.is_group_gift && (
                  <>
                    {item.is_purchased && (
                      <div className="purchased-info">
                        <p className="purchased-by"><strong>This item has been purchased</strong></p>
                      </div>
                    )}

                    {!item.is_purchased && (
                      <div className="item-actions">
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="purchase-link"
                        >
                          Purchase Item
                        </a>
                        <button
                          className="mark-purchased-btn"
                          onClick={() => openPurchaseModal(item)}
                        >
                          Mark as Purchased
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <PurchaseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handlePurchaseConfirm}
        itemName={selectedItem?.name || ""}
      />

      <ContributeModal
        isOpen={contributeModalOpen}
        onClose={() => setContributeModalOpen(false)}
        onConfirm={handleContributionConfirm}
        itemName={selectedItem?.name || ""}
        currentPledged={selectedItem?.current_pledged || 0}
        targetAmount={selectedItem?.group_gift_target || 0}
        venmoLink={selectedItem?.venmo_link}
      />
    </div>
  );
};

export default RegistryPage;
